import { FirebaseError } from "firebase/app";
import {
  browserLocalPersistence,
  connectAuthEmulator,
  getAuth,
  GoogleAuthProvider,
  onAuthStateChanged,
  setPersistence,
  signInAnonymously as firebaseSignInAnonymously,
  signInWithPopup,
  signOut as firebaseSignOut,
  type Auth,
  type User,
} from "firebase/auth";

import { getFirebaseClient } from "../firebase/firebaseClient";

export type AccountType = "anonymous" | "google";

export interface AuthIdentity {
  uid: string;
  accountType: AccountType;
}

export interface AuthService {
  subscribe(listener: (identity: AuthIdentity | null) => void): () => void;
  signInAnonymously(): Promise<AuthIdentity>;
  signInWithGoogle(): Promise<AuthIdentity>;
  signOut(): Promise<void>;
}

let singletonService: AuthService | null | undefined;
let emulatorConnected = false;

function toIdentity(user: User): AuthIdentity {
  return {
    uid: user.uid,
    accountType: user.isAnonymous ? "anonymous" : "google",
  };
}

function authErrorMessage(error: unknown): string {
  if (!(error instanceof FirebaseError)) {
    return "A bejelentkezés most nem sikerült. Kérjük, próbáld újra.";
  }

  switch (error.code) {
    case "auth/popup-closed-by-user":
      return "A Google-belépési ablak bezárult a bejelentkezés előtt.";
    case "auth/popup-blocked":
      return "A böngésző letiltotta a Google-belépési ablakot. Engedélyezd az előugró ablakot, majd próbáld újra.";
    case "auth/network-request-failed":
      return "Hálózati hiba történt. Ellenőrizd az internetkapcsolatot, majd próbáld újra.";
    case "auth/operation-not-allowed":
      return "Ez a belépési mód még nincs engedélyezve a Firebase fejlesztői projektben.";
    case "auth/unauthorized-domain":
      return "Ez a webcím még nincs engedélyezve a Firebase Authentication beállításaiban.";
    case "auth/too-many-requests":
      return "Túl sok belépési próbálkozás történt. Várj egy kicsit, majd próbáld újra.";
    default:
      return "A bejelentkezés most nem sikerült. Kérjük, próbáld újra.";
  }
}

class FirebaseAuthService implements AuthService {
  private readonly persistenceReady: Promise<void>;

  constructor(private readonly auth: Auth) {
    this.persistenceReady = setPersistence(auth, browserLocalPersistence).catch((error: unknown) => {
      throw new Error(authErrorMessage(error));
    });
  }

  subscribe(listener: (identity: AuthIdentity | null) => void): () => void {
    let unsubscribe: () => void = () => undefined;
    let active = true;

    void this.persistenceReady
      .then(() => {
        if (!active) {
          return;
        }
        unsubscribe = onAuthStateChanged(this.auth, (user) => listener(user ? toIdentity(user) : null));
      })
      .catch(() => {
        if (active) {
          listener(null);
        }
      });

    return () => {
      active = false;
      unsubscribe();
    };
  }

  async signInAnonymously(): Promise<AuthIdentity> {
    try {
      await this.persistenceReady;
      if (this.auth.currentUser && !this.auth.currentUser.isAnonymous) {
        await firebaseSignOut(this.auth);
      }
      const credential = await firebaseSignInAnonymously(this.auth);
      return toIdentity(credential.user);
    } catch (error) {
      throw new Error(authErrorMessage(error));
    }
  }

  async signInWithGoogle(): Promise<AuthIdentity> {
    try {
      await this.persistenceReady;

      // Product decision: anonymous and Google users stay separate. No account-linking API is used.
      if (this.auth.currentUser?.isAnonymous) {
        await firebaseSignOut(this.auth);
      }

      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({ prompt: "select_account" });
      const credential = await signInWithPopup(this.auth, provider);
      return toIdentity(credential.user);
    } catch (error) {
      throw new Error(authErrorMessage(error));
    }
  }

  async signOut(): Promise<void> {
    try {
      await this.persistenceReady;
      await firebaseSignOut(this.auth);
    } catch (error) {
      throw new Error(authErrorMessage(error));
    }
  }
}

function createFirebaseAuthService(): AuthService | null {
  const client = getFirebaseClient();
  if (!client) {
    return null;
  }

  const auth = getAuth(client.app);

  if (client.settings.useAuthEmulator && !emulatorConnected) {
    connectAuthEmulator(auth, client.settings.authEmulatorUrl, { disableWarnings: true });
    emulatorConnected = true;
  }

  return new FirebaseAuthService(auth);
}

export function getFirebaseAuthService(): AuthService | null {
  if (singletonService === undefined) {
    singletonService = createFirebaseAuthService();
  }
  return singletonService;
}
